import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { memo, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueries } from 'react-query';
import {
  getContentfulArticleCards,
  getContentfulRecipeCardCards,
  getContentfulRecipeCards,
  getContentfulVideoCards,
} from '../../../lib/contentful/getContentfulContent';
import {
  buildCategoryItemUrlByKey,
  getCategoryBaseUrlByKey,
  getCategoryMetaByKey,
} from '../../../lib/constants/learningCenterMeta';
import { ContentCard } from './ContentCard';

// Singleton QueryClient for this module to avoid repeated refetching across usages
const lcQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      keepPreviousData: true,
    },
  },
});

const CategoriesListInner = memo(function CategoriesListInner({ categoriesKeys, limit }) {
  const fetchersByKey = useMemo(
    () => ({
      recipeCards: getContentfulRecipeCardCards,
      articles: getContentfulArticleCards,
      recipes: getContentfulRecipeCards,
      videos: getContentfulVideoCards,
    }),
    [],
  );

  const queries = useQueries(
    categoriesKeys.map((key) => {
      const fn = fetchersByKey[key];
      return {
        queryKey: ['learning-center', key, limit],
        queryFn: async () => {
          const data = fn ? await fn() : [];
          return data.slice(0, limit);
        },
        staleTime: 5 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        keepPreviousData: true,
      };
    }),
  );

  const isLoading = queries.some((q) => q.isLoading);
  const anyError = queries.find((q) => q.isError);

  const dataByKey = useMemo(() => {
    const out = {};
    categoriesKeys.forEach((key, idx) => {
      out[key] = queries[idx]?.data || [];
    });
    return out;
  }, [queries, categoriesKeys]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (anyError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <Typography color="error">Failed to load content.</Typography>
      </Box>
    );
  }

  return (
    <>
      {categoriesKeys.map((categoryKey) => {
        const meta = getCategoryMetaByKey(categoryKey);
        const data = dataByKey[categoryKey] || [];
        return (
          <React.Fragment key={categoryKey}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 1 }}
              >
                {meta.title}
              </Typography>
              <Button
                size="small"
                component={RouterLink}
                to={getCategoryBaseUrlByKey(categoryKey)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    sx={{
                      color: 'var(--primary-main, #211551)',
                      fontFamily: 'Montserrat',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 500,
                      lineHeight: '22px',
                      letterSpacing: '0.46px',
                      textTransform: 'uppercase',
                    }}
                  >
                    VIEW ALL
                  </Typography>
                  <ArrowForwardIcon sx={{ width: 18, height: 18, flexShrink: 0, marginLeft: '4px' }} />
                </Box>
              </Button>
            </Box>
            <Divider sx={{ borderColor: '#E0E0E0', mb: 2 }} />
            <Grid
              container
              spacing={2}
              sx={{ marginTop: '10px', marginBottom: '25px', alignItems: 'center', justifyContent: 'center' }}
            >
              {data.map((item) => (
                <ContentCard
                  key={item.id}
                  title={item.title}
                  imageUrl={item.imageUrl}
                  linkPath={buildCategoryItemUrlByKey(categoryKey, item.id)}
                />
              ))}
            </Grid>
          </React.Fragment>
        );
      })}
    </>
  );
});

/**
 * Reusable list of learning center categories with cards.
 * Props:
 * - categoriesKeys: string[] keys from learningCenterMeta (default: ['recipeCards','articles','videos','recipes'])
 * - limit: number of items per category (default: 4)
 */
export const LearningCenterCategories = memo(function LearningCenterCategories({
  categoriesKeys = ['recipeCards', 'articles', 'videos', 'recipes'],
  limit = 4,
}) {
  return (
    <QueryClientProvider client={lcQueryClient}>
      <CategoriesListInner
        categoriesKeys={categoriesKeys}
        limit={limit}
      />
    </QueryClientProvider>
  );
});
